import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, type ReactNode } from "react";
import { useInsightWorkspaceSplit } from "./useInsightWorkspaceSplit";
import type { InsightAnalysisTexts } from "../config/analysisInsightConfig";
import { computePatientCountMaps } from "../patients/computeInsightPatientCounts";
import { InsightFilterActiveFooter } from "../filter/InsightFilterActiveFooter";
import { InsightFilterPanelHeader } from "../filter/InsightFilterPanelHeader";
import { InsightRankingPanel } from "../filter/InsightRankingPanel";
import { InsightPatientsPanel } from "../patients/InsightPatientsPanel";
import { InsightStatusPills } from "../filter/InsightStatusPills";
import { InsightWorkspaceLoadingSkeleton } from "./InsightWorkspaceLoadingSkeleton";
import { InsightWorkspaceResultBody } from "./InsightWorkspaceResultBody";
import { InsightWorkspaceFilterColumn } from "./InsightWorkspaceFilterColumn";
import { InsightWorkspaceResultColumn } from "./InsightWorkspaceResultColumn";
import { InsightWorkspaceSplitDivider } from "./InsightWorkspaceSplitDivider";
import { useInsightSelection } from "../filter/useInsightSelection";
import { LEFT_BLOCK_GAP, WORKSPACE_SPLIT_GUTTER } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { LoadPhase, PatientsProgress } from "./loadPhase";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

interface InsightWorkspaceProps {
  accentColor: string;
  texts: InsightAnalysisTexts;
  practitionerFilter?: ReactNode;
  filterResetKey: string;
  loadPhase: LoadPhase;
  patientsProgress: PatientsProgress;
  hasData: boolean;
  isEmpty: boolean;
  emptyPeriodLabel: string;
  emptyPractitionerLabel: string;
  showClearPractitionerAction: boolean;
  onClearPractitioner?: () => void;
  onPreviousPeriod?: () => void;
  onRetry: () => void;
  statusChart?: { insightText: string; items: InsightChartItem[] };
  rankingChart?: { insightText?: string; items: InsightChartItem[] };
  secondaryStatusChart?: { title: string; insightText?: string; items: InsightChartItem[] };
  patientRows: InsightPatientRow[];
  statusLabelByKey: Record<string, string>;
  patientPanelPlaceholder?: ReactNode;
  onFilterChange?: (filters: {
    statusKeys: string[];
    itemKeys: string[];
    secondaryKeys: string[];
  }) => void;
  onPatientStatsChange?: (filtered: number, total: number) => void;
  /** Default: Evoluir visível na linha. */
  showEvolve?: boolean;
}

export function InsightWorkspace({
  accentColor,
  texts,
  practitionerFilter,
  filterResetKey,
  loadPhase,
  patientsProgress,
  hasData,
  isEmpty,
  emptyPeriodLabel,
  emptyPractitionerLabel,
  showClearPractitionerAction,
  onClearPractitioner,
  onPreviousPeriod,
  onRetry,
  statusChart,
  rankingChart,
  secondaryStatusChart,
  patientRows,
  statusLabelByKey,
  patientPanelPlaceholder,
  onFilterChange,
  onPatientStatsChange,
  showEvolve = true,
}: InsightWorkspaceProps) {
  const theme = useTheme();
  const isSplitLayout = useMediaQuery(theme.breakpoints.up("md"));
  const {
    containerRef,
    leftPercent,
    onDividerPointerDown,
    onDividerPointerMove,
    onDividerPointerUp,
  } = useInsightWorkspaceSplit();

  const {
    statusKeys,
    itemKeys,
    secondaryKeys,
    toggleStatus,
    toggleItem,
    toggleSecondary,
    clear,
    filterRows,
  } = useInsightSelection(filterResetKey);

  const { patientCountByStatus, patientCountByItem, patientCountBySecondary } =
    useMemo(() => computePatientCountMaps(patientRows), [patientRows]);

  const statusTotal =
    statusChart?.items.reduce((acc, item) => acc + item.value, 0) ?? 0;
  const secondaryTotal =
    secondaryStatusChart?.items.reduce((acc, item) => acc + item.value, 0) ?? 0;

  const filteredPatientRows = useMemo(
    () => filterRows(patientRows),
    [filterRows, patientRows]
  );

  const hasActiveFilter =
    statusKeys.length > 0 || itemKeys.length > 0 || secondaryKeys.length > 0;
  const activeFilterCount =
    statusKeys.length + itemKeys.length + secondaryKeys.length;
  const filterSignature = useMemo(
    () =>
      [...statusKeys].sort().join("|") +
      ";" +
      [...itemKeys].sort().join("|") +
      ";" +
      [...secondaryKeys].sort().join("|"),
    [statusKeys, itemKeys, secondaryKeys]
  );

  const showInitialSkeleton =
    !hasData && (loadPhase === "records" || loadPhase === "idle");
  const staleOverlay =
    hasData && (loadPhase === "records" || loadPhase === "patients");
  const filtersDisabled = isEmpty && loadPhase === "done";

  useEffect(() => {
    onPatientStatsChange?.(filteredPatientRows.length, patientRows.length);
  }, [filteredPatientRows.length, patientRows.length, onPatientStatsChange]);

  useEffect(() => {
    onFilterChange?.({ statusKeys, itemKeys, secondaryKeys });
  }, [statusKeys, itemKeys, secondaryKeys, onFilterChange]);

  const resultShowsTable =
    hasData &&
    !showInitialSkeleton &&
    loadPhase !== "error" &&
    loadPhase !== "patients" &&
    !(loadPhase === "done" && isEmpty);

  const leftColumnInner = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: LEFT_BLOCK_GAP }}>
      <InsightFilterPanelHeader practitionerFilter={practitionerFilter} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: LEFT_BLOCK_GAP,
          opacity: staleOverlay ? 0.5 : 1,
          pointerEvents: staleOverlay ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}
      >
        {showInitialSkeleton ? (
          <InsightWorkspaceLoadingSkeleton loadingText={texts.loadingText} />
        ) : (
          <>
            {statusChart ? (
              <InsightStatusPills
                sectionTitle="Situação"
                insightText={statusChart.insightText}
                items={statusChart.items}
                selectedKeys={statusKeys}
                accentColor={accentColor}
                totalForPercent={statusTotal}
                patientCountByKey={patientCountByStatus}
                itemUnitLabel={texts.itemPlural}
                onToggle={toggleStatus}
                disabled={filtersDisabled}
              />
            ) : null}
            {secondaryStatusChart ? (
              <InsightStatusPills
                sectionTitle={secondaryStatusChart.title}
                insightText={secondaryStatusChart.insightText}
                items={secondaryStatusChart.items}
                selectedKeys={secondaryKeys}
                accentColor={accentColor}
                totalForPercent={secondaryTotal}
                patientCountByKey={patientCountBySecondary}
                itemUnitLabel={texts.itemPlural}
                onToggle={toggleSecondary}
                disabled={filtersDisabled}
              />
            ) : null}
            {rankingChart ? (
              <InsightRankingPanel
                sectionTitle={texts.rankingTitle}
                insightText={rankingChart.insightText ?? texts.rankingHint}
                searchPlaceholder={texts.searchPlaceholder}
                rankingScopeAriaLabel={texts.rankingScopeAriaLabel}
                items={rankingChart.items}
                selectedKeys={itemKeys}
                accentColor={accentColor}
                patientCountByKey={patientCountByItem}
                itemUnitLabel={texts.itemPlural}
                onToggle={toggleItem}
                disabled={filtersDisabled}
              />
            ) : null}
          </>
        )}
      </Box>
      <InsightFilterActiveFooter
        accentColor={accentColor}
        activeFilterCount={activeFilterCount}
        onClear={clear}
      />
    </Box>
  );

  const leftColumn = (
    <Box
      sx={{
        ...(isSplitLayout
          ? {
              flex: `0 1 ${leftPercent}%`,
              width: `${leftPercent}%`,
              maxWidth: `${leftPercent}%`,
            }
          : { width: "100%", flex: "1 1 auto" }),
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        display: "flex",
      }}
    >
      <InsightWorkspaceFilterColumn
        accentColor={accentColor}
        hasActiveFilter={hasActiveFilter}
      >
        {leftColumnInner}
      </InsightWorkspaceFilterColumn>
    </Box>
  );

  const rightColumn = (
    <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 0, height: "100%", display: "flex" }}>
      <InsightWorkspaceResultColumn
        accentColor={accentColor}
        hasActiveFilter={hasActiveFilter}
        filterSignature={filterSignature}
      >
        <InsightWorkspaceResultBody
          loadPhase={loadPhase}
          patientsProgress={patientsProgress}
          accentColor={accentColor}
          isEmpty={isEmpty}
          emptyPeriodLabel={emptyPeriodLabel}
          emptyPractitionerLabel={emptyPractitionerLabel}
          emptyListPrefix={texts.emptyListPrefix}
          errorText={texts.errorText}
          showClearPractitionerAction={showClearPractitionerAction}
          onClearPractitioner={() => onClearPractitioner?.()}
          onPreviousPeriod={() => onPreviousPeriod?.()}
          onRetry={onRetry}
          initialSkeleton={showInitialSkeleton}
        >
          {resultShowsTable ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                opacity: staleOverlay ? 0.5 : 1,
                pointerEvents: staleOverlay ? "none" : "auto",
                transition: "opacity 0.2s ease",
              }}
            >
              {filteredPatientRows.length === 0 && patientPanelPlaceholder ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 4,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  {patientPanelPlaceholder}
                </Box>
              ) : (
                <InsightPatientsPanel
                  accentColor={accentColor}
                  rows={filteredPatientRows}
                  totalPatientCount={patientRows.length}
                  hasActiveFilter={hasActiveFilter}
                  statusLabelByKey={statusLabelByKey}
                  texts={texts}
                  fillHeight
                  showEvolve={showEvolve}
                />
              )}
            </Box>
          ) : null}
        </InsightWorkspaceResultBody>
      </InsightWorkspaceResultColumn>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        height: "100%",
        minHeight: 0,
        gap: { xs: 3, md: WORKSPACE_SPLIT_GUTTER },
      }}
    >
      {leftColumn}
      {isSplitLayout ? (
        <InsightWorkspaceSplitDivider
          accentColor={accentColor}
          onPointerDown={onDividerPointerDown}
          onPointerMove={onDividerPointerMove}
          onPointerUp={onDividerPointerUp}
        />
      ) : null}
      {rightColumn}
    </Box>
  );
}
